import makeStyles from '@mui/styles/makeStyles';
import { stepContainer, stepSection } from '../../../shared/ui/styles';

// Figma select-children screen (272:1695): 24px between the section title,
// the info strip and the list; the list has no surrounding card - rows are
// 46px tall, full-bleed, separated by 1px dividers.
export const createClasses = makeStyles(theme => ({
  container: {
    ...stepContainer(theme),
    gap: theme.spacing(3)
  },
  header: {
    ...stepSection(theme),
    gap: theme.spacing(3)
  },
  title: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '21px'
  },
  // Info strip explaining the bulk migration - Secondary/100 background,
  // caption text (12/15) in Primary/500, like the other flow info boxes.
  infoBody: {
    backgroundColor: theme.palette.secondary[100],
    color: theme.palette.primary[500],
    fontSize: '12px',
    lineHeight: '15px',
    borderRadius: '8px',
    padding: theme.spacing(2)
  },
  panel: {
    display: 'flex',
    flexDirection: 'column' as const
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '4px',
    paddingBottom: theme.spacing(2)
  },
  panelTitle: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '21px',
    color: theme.palette.text.primary
  },
  // In this theme the link/accent blue is secondary[500] (primary.main is
  // the dark navy) - the Figma count and Select All link are blue.
  panelCount: {
    color: theme.palette.secondary[500],
    marginLeft: theme.spacing(0.25)
  },
  selectAllBtn: {
    minWidth: 0,
    padding: 0,
    color: theme.palette.secondary[500],
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: 320,
    overflowY: 'auto' as const
  },
  // Figma row: 46px tall, 16px side padding, 16px checkbox + 8px gap so the
  // name starts 40px in; hover fills the row with Secondary/100.
  row: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing(1),
    margin: 0,
    height: '46px',
    padding: theme.spacing(0, 2),
    transition: 'background-color 120ms ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.secondary[100]
    },
    '& .MuiCheckbox-root': {
      padding: 0
    }
  },
  // Past the cap the remaining rows stay readable but stop offering themselves:
  // no hover fill, no pointer, dimmed text - the checkbox is disabled anyway.
  rowUnavailable: {
    cursor: 'default',
    opacity: 0.5,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  divider: {
    backgroundColor: theme.palette.divider
  },
  footer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(1.5)
  },
  // At the ceiling the footer becomes the reason the rest of the list is dead,
  // so it takes the tinted-panel-with-a-rule treatment the result screen uses
  // for the same job. The amber is background and border only: every shade of
  // palette.warning fails WCAG 1.4.3 as 12px text on a light surface (warning
  // [500] is ~1.7:1, and even warning[800] only reaches ~2.7:1), which would
  // make this hardest to read exactly when it starts mattering.
  // Doubled selector so it beats `footer`'s paddingTop on specificity rather
  // than on declaration order, which reordering this object would silently flip.
  footerAtLimit: {
    '&&': {
      padding: theme.spacing(1.5, 2),
      marginTop: theme.spacing(1.5),
      borderRadius: '4px',
      borderLeft: `3px solid ${theme.palette.warning[500]}`,
      backgroundColor: theme.palette.warning[200]
    }
  },
  count: {
    color: theme.palette.text.secondary
  },
  countAtLimit: {
    color: theme.palette.text.primary,
    fontWeight: 500
  },
  limitNote: {
    color: theme.palette.text.secondary
  },
  nameAndTag: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1
  },
  cancelledTag: {
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[200],
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4)
  }
}));
